import type { ExpansionId, RoleId, TeamId } from './ids'

export type RoleTag =
  | 'wolf' // counts as a werewolf card for scoring and for "who is a wolf"
  | 'awakeWolf' // wakes with the werewolves
  | 'alien'
  | 'seer' // Beholder looks at these
  | 'evil' // wolves, aliens and vampires: the cards the village must kill (a card's team says whose)
  | 'tanner'
  | 'individualWin' // wins as a player, not as a team
  | 'copying' // takes another card's role/team
  | 'needsNumbers' // seat number tokens are dealt when this role is in play
  | 'marks' // every player gets a Mark of Clarity when this role is in play (Vampire)
  | 'vampire' // a Vampire card: wakes with the Vampires at dusk, and Renfield looks for these

/**
 * What a night step reads and writes. The scheduler batches steps whose reads never intersect earlier writes.
 * `tokens` are the shields, artifacts and seat numbers; `marks` the one mark in front of every player.
 */
export type AccessKey =
  | 'seatCards'
  | 'centerCards'
  | 'tokens'
  | 'marks'
  | 'reveals'
  | 'identities'
  | 'touched'
  | 'pointing' // everyone pointing for the Empath

export interface NightStepDef {
  /** Program id resolved by @onuw/engine (e.g. "wolves.wake"). */
  id: string
  /** Official wake order; lower wakes first. Fractional numbers keep related steps adjacent. */
  order: number
  reads: AccessKey[]
  writes: AccessKey[]
  /** Never share a batch with anyone (the step changes what other steps exist). */
  barrier?: true
  /** Reads the final layout: scheduled after every seat/center writer. */
  final?: true
  /** Base timeout for select prompts in this step, seconds, before the pace multiplier. */
  timeoutSec: number
  /** When the role's card is in the center: fake the step so timing/announcements leak nothing. */
  phantom?: { minSec: number; maxSec: number; announce?: boolean }
  /** Position in the official order is ported from the old registry and still needs confirming. */
  confirm?: true
}

export interface VoteRules {
  /** Vote weight (default 1). */
  weight?: number
  /** Cannot be killed by votes (Prince). */
  immune?: true
  /** The player this role votes for cannot be killed (Bodyguard). */
  protects?: true
  /** If killed, the player it voted for dies too (Hunter). */
  hunter?: true
  /** Becomes a werewolf if a werewolf votes for it (Cursed). */
  cursed?: true
  /** Dies if every werewolf votes for it and the village loses (Leader). */
  leader?: true
  /** Cannot be killed when another Vampire votes for it (The Master). */
  savedByVampires?: true
}

export interface RoleDef {
  id: RoleId
  /** Printed team. 'variable' means "whatever it copies". */
  team: TeamId
  expansion: ExpansionId
  /** Minimum copies once selected (Masons come in pairs). */
  min?: number
  /** Maximum copies in one game (default 1). */
  max?: number
  requires?: { role: RoleId; min: number }[]
  /** Adds one center card (Alpha Wolf's sideways werewolf). */
  extraCenter?: 1
  tags: RoleTag[]
  /** Weighted random-action table drawn once per game. */
  randoms?: { id: string; weight: number }[]
  night?: NightStepDef[]
  vote?: VoteRules
}

type StepBody = Omit<NightStepDef, 'id'>
const select = (
  order: number,
  reads: AccessKey[],
  writes: AccessKey[],
  extra: Partial<StepBody> = {},
): StepBody => ({
  order,
  reads,
  writes,
  timeoutSec: 40,
  ...extra,
})
const info = (order: number, reads: AccessKey[], extra: Partial<StepBody> = {}): StepBody => ({
  order,
  reads,
  writes: [],
  timeoutSec: 15,
  ...extra,
})
const step = (id: string, s: StepBody): NightStepDef => ({ id, ...s })

/** The Vampires (the Count and the Master too) wake together and give one player the Mark of the Vampire. */
const VAMPIRES_WAKE = () =>
  step('vampires.wake', select(0.11, ['identities'], ['marks'], { phantom: { minSec: 3, maxSec: 10 } }))

/** Everything alien wakes together (Groob, Zerb, the Synthetic and the Body Snatcher too) and gets an action. */
const ALIENS_WAKE = () =>
  step(
    'alien.wake',
    select(1.5, ['identities', 'tokens', 'seatCards', 'centerCards'], ['touched', 'seatCards'], {
      barrier: true,
      phantom: { minSec: 4, maxSec: 12, announce: true },
      confirm: true,
    }),
  )

/** Groob and Zerb wake again after the Aliens and look for each other. */
const GROOB_ZERB_WAKE = () =>
  step('groobZerb.wake', info(1.65, ['identities'], { phantom: { minSec: 3, maxSec: 8 } }))

const WOLVES_WAKE = () =>
  step(
    'wolves.wake',
    select(2.0, ['identities', 'centerCards', 'tokens'], ['touched'], { phantom: { minSec: 3, maxSec: 10 } }),
  )

export const ROLES: Record<RoleId, RoleDef> = {
  oracle: {
    id: 'oracle',
    team: 'village',
    expansion: 'alien',
    tags: ['needsNumbers'],
    randoms: [
      { id: 'changeTeam', weight: 45 },
      { id: 'center', weight: 10 },
      { id: 'exchange', weight: 10 },
      { id: 'evenOdd', weight: 10 },
    ],
    night: [
      step(
        'oracle.question',
        select(0.0, ['centerCards', 'seatCards'], ['identities', 'touched', 'seatCards', 'centerCards'], {
          barrier: true,
          phantom: { minSec: 4, maxSec: 12, announce: true },
          confirm: true,
        }),
      ),
    ],
  },
  copycat: {
    id: 'copycat',
    team: 'variable',
    expansion: 'vampire',
    tags: ['copying'],
    night: [
      step(
        'copycat.copy',
        select(0.1, ['centerCards'], ['identities', 'touched'], {
          barrier: true,
          phantom: { minSec: 3, maxSec: 8 },
        }),
      ),
    ],
  },
  doppelganger: {
    id: 'doppelganger',
    team: 'variable',
    expansion: 'base',
    tags: ['copying'],
    night: [
      // looks at another player's card and becomes that role; the roles she does "immediately" run inside this
      // step, so it may read and move anything they can (see DOPPELGANGER_PLAY). Before the Vampires when marks
      // are in play (the planner moves it to DOPPELGANGER_DUSK_ORDER), after the Sentinel otherwise.
      step(
        'doppelganger.copy',
        select(
          1.0,
          ['seatCards', 'centerCards', 'tokens', 'marks', 'identities'],
          ['identities', 'seatCards', 'centerCards', 'tokens', 'marks', 'touched'],
          { barrier: true, phantom: { minSec: 3, maxSec: 10 } },
        ),
      ),
    ],
  },
  sentinel: {
    id: 'sentinel',
    team: 'village',
    expansion: 'daybreak',
    tags: [],
    night: [
      step('sentinel.shield', select(0.2, ['tokens'], ['tokens'], { phantom: { minSec: 3, maxSec: 8 } })),
    ],
  },
  alien: {
    id: 'alien',
    team: 'alien',
    expansion: 'alien',
    max: 2,
    tags: ['alien', 'evil', 'needsNumbers'],
    randoms: [
      { id: 'viewAnyPlayer', weight: 10 },
      { id: 'viewAnyCenter', weight: 5 },
      { id: 'allAlienViewAnyCenter', weight: 5 },
      { id: 'allAlienViewAnyPlayer', weight: 5 },
      { id: 'viewNeighbor', weight: 5 },
      { id: 'viewEven', weight: 5 },
      { id: 'viewOdd', weight: 5 },
      { id: 'viewHigher', weight: 5 },
      { id: 'viewLower', weight: 5 },
      { id: 'doNothing', weight: 20 },
      { id: 'giveToLeft', weight: 10 },
      { id: 'giveToRight', weight: 10 },
      { id: 'revealCard', weight: 10 },
    ],
    night: [ALIENS_WAKE()],
  },
  werewolf: {
    id: 'werewolf',
    team: 'werewolves',
    expansion: 'base',
    max: 2,
    tags: ['wolf', 'awakeWolf', 'evil'],
    night: [WOLVES_WAKE()],
  },
  giantWolf: {
    id: 'giantWolf',
    team: 'werewolves',
    expansion: 'custom',
    tags: ['wolf', 'awakeWolf', 'evil'],
    night: [WOLVES_WAKE()],
    vote: { weight: 2 },
  },
  alphaWolf: {
    id: 'alphaWolf',
    team: 'werewolves',
    expansion: 'daybreak',
    requires: [{ role: 'werewolf', min: 1 }],
    extraCenter: 1,
    tags: ['wolf', 'awakeWolf', 'evil'],
    night: [
      WOLVES_WAKE(),
      step(
        'alphaWolf.swap',
        select(2.1, ['tokens'], ['seatCards', 'centerCards', 'touched'], {
          phantom: { minSec: 3, maxSec: 8 },
        }),
      ),
    ],
  },
  mysticWolf: {
    id: 'mysticWolf',
    team: 'werewolves',
    expansion: 'daybreak',
    tags: ['wolf', 'awakeWolf', 'evil'],
    night: [
      WOLVES_WAKE(),
      step(
        'mysticWolf.view',
        select(2.2, ['seatCards', 'tokens'], ['touched'], { phantom: { minSec: 3, maxSec: 8 } }),
      ),
    ],
  },
  minion: {
    id: 'minion',
    team: 'werewolves',
    expansion: 'base',
    tags: [],
    night: [step('minion.wake', info(3.0, ['identities'], { phantom: { minSec: 3, maxSec: 8 } }))],
  },
  appTanner: {
    id: 'appTanner',
    team: 'tanner',
    expansion: 'bonus',
    tags: ['tanner', 'individualWin'],
    night: [
      step('appTanner.wake', info(3.1, ['identities'], { phantom: { minSec: 3, maxSec: 8 }, confirm: true })),
    ],
  },
  leader: {
    id: 'leader',
    team: 'village',
    expansion: 'alien',
    tags: [],
    night: [
      step('leader.wake', info(3.2, ['identities'], { phantom: { minSec: 3, maxSec: 8 }, confirm: true })),
    ],
    vote: { leader: true },
  },
  cow: {
    id: 'cow',
    team: 'village',
    expansion: 'alien',
    tags: [],
    night: [
      step('cow.wake', info(1.6, ['identities'], { phantom: { minSec: 3, maxSec: 8 }, confirm: true })),
    ],
  },
  mason: {
    id: 'mason',
    team: 'village',
    expansion: 'base',
    min: 2,
    max: 2,
    tags: [],
    night: [step('mason.wake', info(4.0, ['identities'], { phantom: { minSec: 3, maxSec: 8 } }))],
  },
  thing: {
    id: 'thing',
    team: 'village',
    expansion: 'bonus',
    tags: [],
    night: [step('thing.touch', select(4.1, ['tokens'], [], { phantom: { minSec: 3, maxSec: 8 } }))],
  },
  seer: {
    id: 'seer',
    team: 'village',
    expansion: 'base',
    tags: ['seer'],
    night: [
      step(
        'seer.view',
        select(5.0, ['seatCards', 'centerCards', 'tokens'], ['touched'], {
          phantom: { minSec: 3, maxSec: 10 },
        }),
      ),
    ],
  },
  appSeer: {
    id: 'appSeer',
    team: 'village',
    expansion: 'daybreak',
    tags: ['seer'],
    night: [
      step('appSeer.view', select(5.1, ['centerCards'], ['touched'], { phantom: { minSec: 3, maxSec: 8 } })),
    ],
  },
  paranormalInvestigator: {
    id: 'paranormalInvestigator',
    team: 'village',
    expansion: 'daybreak',
    tags: [],
    night: [
      step(
        'paranormalInvestigator.view',
        select(5.2, ['seatCards', 'tokens'], ['identities', 'touched'], {
          phantom: { minSec: 4, maxSec: 12 },
        }),
      ),
    ],
  },
  nostradamus: {
    id: 'nostradamus',
    team: 'village',
    expansion: 'bonus',
    tags: [],
    night: [
      step(
        'nostradamus.view',
        select(5.3, ['seatCards', 'tokens'], ['identities', 'touched'], {
          barrier: true,
          phantom: { minSec: 4, maxSec: 12, announce: true },
          confirm: true,
        }),
      ),
    ],
  },
  psychic: {
    id: 'psychic',
    team: 'village',
    expansion: 'alien',
    tags: ['needsNumbers'],
    randoms: [
      { id: 'viewOnePlayer', weight: 300 },
      { id: 'viewTwoPlayers', weight: 25 },
      { id: 'viewOneNeighbor', weight: 50 },
      { id: 'viewTwoNeighbors', weight: 25 },
      { id: 'viewEven', weight: 50 },
      { id: 'viewOdd', weight: 50 },
      { id: 'viewHigher', weight: 100 },
      { id: 'viewLower', weight: 100 },
      { id: 'viewOneCenter', weight: 250 },
      { id: 'viewTwoCenter', weight: 50 },
    ],
    night: [
      step(
        'psychic.view',
        select(5.4, ['seatCards', 'centerCards', 'tokens'], ['touched'], {
          phantom: { minSec: 4, maxSec: 12, announce: true },
          confirm: true,
        }),
      ),
    ],
  },
  robber: {
    id: 'robber',
    team: 'village',
    expansion: 'base',
    tags: [],
    night: [
      step(
        'robber.rob',
        select(6.0, ['seatCards', 'tokens'], ['seatCards', 'touched'], {
          phantom: { minSec: 3, maxSec: 10 },
        }),
      ),
    ],
  },
  witch: {
    id: 'witch',
    team: 'village',
    expansion: 'daybreak',
    tags: [],
    night: [
      step(
        'witch.brew',
        select(6.1, ['centerCards', 'tokens'], ['seatCards', 'centerCards', 'touched'], {
          phantom: { minSec: 4, maxSec: 12 },
        }),
      ),
    ],
  },
  troublemaker: {
    id: 'troublemaker',
    team: 'village',
    expansion: 'base',
    tags: [],
    night: [
      step(
        'troublemaker.swap',
        select(7.0, ['tokens'], ['seatCards', 'touched'], { phantom: { minSec: 3, maxSec: 10 } }),
      ),
    ],
  },
  auraSeer: {
    id: 'auraSeer',
    team: 'village',
    expansion: 'bonus',
    tags: [],
    night: [step('auraSeer.wake', info(7.1, ['touched'], { phantom: { minSec: 3, maxSec: 8 } }))],
  },
  gremlin: {
    id: 'gremlin',
    team: 'village',
    expansion: 'vampire',
    tags: [],
    night: [
      // with marks in play the Gremlin swaps two players' cards or their marks
      step(
        'gremlin.swap',
        select(7.2, ['tokens'], ['seatCards', 'marks', 'touched'], { phantom: { minSec: 3, maxSec: 10 } }),
      ),
    ],
  },
  drunk: {
    id: 'drunk',
    team: 'village',
    expansion: 'base',
    tags: [],
    night: [
      step(
        'drunk.swap',
        select(8.0, ['tokens'], ['seatCards', 'centerCards', 'touched'], {
          phantom: { minSec: 3, maxSec: 8 },
        }),
      ),
    ],
  },
  insomniac: {
    id: 'insomniac',
    team: 'village',
    expansion: 'base',
    tags: [],
    night: [
      step('insomniac.wake', info(9.0, ['seatCards'], { final: true, phantom: { minSec: 3, maxSec: 8 } })),
    ],
  },
  beholder: {
    id: 'beholder',
    team: 'village',
    expansion: 'bonus',
    tags: [],
    night: [
      step(
        'beholder.wake',
        info(9.2, ['seatCards', 'identities'], { final: true, phantom: { minSec: 3, maxSec: 8 } }),
      ),
    ],
  },
  squire: {
    id: 'squire',
    team: 'werewolves',
    expansion: 'bonus',
    tags: [],
    night: [
      step(
        'squire.wake',
        info(9.1, ['seatCards', 'identities'], { final: true, phantom: { minSec: 3, maxSec: 8 } }),
      ),
    ],
  },
  revealer: {
    id: 'revealer',
    team: 'village',
    expansion: 'daybreak',
    tags: [],
    night: [
      step(
        'revealer.reveal',
        select(9.3, ['seatCards', 'tokens'], ['reveals', 'touched'], {
          final: true,
          phantom: { minSec: 3, maxSec: 10 },
        }),
      ),
    ],
  },
  exposer: {
    id: 'exposer',
    team: 'village',
    expansion: 'alien',
    tags: [],
    randoms: [
      { id: 'exposeOne', weight: 60 },
      { id: 'exposeTwo', weight: 30 },
      { id: 'exposeThree', weight: 10 },
    ],
    night: [
      step(
        'exposer.expose',
        select(9.4, ['centerCards'], ['reveals', 'touched'], {
          final: true,
          phantom: { minSec: 3, maxSec: 10, announce: true },
          confirm: true,
        }),
      ),
    ],
  },
  blob: {
    id: 'blob',
    team: 'blob',
    expansion: 'alien',
    tags: ['individualWin'],
    randoms: [
      { id: 'rightBlob', weight: 50 },
      { id: 'leftBlob', weight: 50 },
    ],
    night: [
      step(
        'blob.wake',
        info(9.5, ['identities'], { phantom: { minSec: 3, maxSec: 8, announce: true }, confirm: true }),
      ),
    ],
  },
  mortician: {
    id: 'mortician',
    team: 'mortician',
    expansion: 'alien',
    tags: ['individualWin'],
    randoms: [
      { id: 'viewOneNeighbor', weight: 40 },
      { id: 'viewTwoNeighbors', weight: 10 },
      { id: 'viewRightNeighbor', weight: 20 },
      { id: 'viewLeftNeighbor', weight: 20 },
      { id: 'viewSelf', weight: 10 },
    ],
    night: [
      step(
        'mortician.view',
        select(9.6, ['seatCards', 'tokens'], ['touched'], {
          final: true,
          phantom: { minSec: 3, maxSec: 10, announce: true },
          confirm: true,
        }),
      ),
    ],
  },
  curator: {
    id: 'curator',
    team: 'village',
    expansion: 'daybreak',
    tags: [],
    night: [
      step(
        'curator.artifact',
        select(9.45, ['tokens'], ['tokens', 'identities'], { phantom: { minSec: 3, maxSec: 8 } }),
      ),
    ],
  },
  villager: { id: 'villager', team: 'village', expansion: 'base', max: 3, tags: [] },
  dreamWolf: { id: 'dreamWolf', team: 'werewolves', expansion: 'daybreak', tags: ['wolf', 'evil'] },
  hunter: { id: 'hunter', team: 'village', expansion: 'base', tags: [], vote: { hunter: true } },
  bodyguard: { id: 'bodyguard', team: 'village', expansion: 'daybreak', tags: [], vote: { protects: true } },
  prince: { id: 'prince', team: 'village', expansion: 'bonus', tags: [], vote: { immune: true } },
  tanner: { id: 'tanner', team: 'tanner', expansion: 'base', tags: ['tanner', 'individualWin'] },
  cursed: { id: 'cursed', team: 'village', expansion: 'bonus', tags: [], vote: { cursed: true } },
  // Vampire. Dusk comes before the night: these roles hand out marks (a mark placed on a player replaces theirs),
  // and at the end of dusk everyone views their own mark (TABLE_STEPS). Mark moves are applied in wake order when
  // the batch ends, so the dusk roles act at the same time.
  renfield: {
    id: 'renfield',
    // on the village team while no player is a Vampire (scoring)
    team: 'vampire',
    expansion: 'vampire',
    tags: ['marks'],
    night: [
      step(
        'renfield.wake',
        info(0.12, ['identities', 'marks'], { writes: ['marks'], phantom: { minSec: 3, maxSec: 8 } }),
      ),
    ],
  },
  diseased: {
    id: 'diseased',
    team: 'village',
    expansion: 'vampire',
    tags: ['marks'],
    night: [step('diseased.mark', select(0.13, [], ['marks'], { phantom: { minSec: 3, maxSec: 8 } }))],
  },
  cupid: {
    id: 'cupid',
    team: 'village',
    expansion: 'vampire',
    tags: ['marks'],
    night: [step('cupid.love', select(0.14, [], ['marks'], { phantom: { minSec: 3, maxSec: 10 } }))],
  },
  instigator: {
    id: 'instigator',
    team: 'village',
    expansion: 'vampire',
    tags: ['marks'],
    night: [step('instigator.mark', select(0.15, [], ['marks'], { phantom: { minSec: 3, maxSec: 8 } }))],
  },
  priest: {
    id: 'priest',
    team: 'village',
    expansion: 'vampire',
    tags: ['marks'],
    night: [step('priest.cleanse', select(0.16, [], ['marks'], { phantom: { minSec: 3, maxSec: 8 } }))],
  },
  assassin: {
    id: 'assassin',
    team: 'assassin',
    expansion: 'vampire',
    tags: ['individualWin', 'marks'],
    night: [
      step('assassin.mark', select(0.17, ['identities'], ['marks'], { phantom: { minSec: 3, maxSec: 8 } })),
    ],
  },
  appAssassin: {
    id: 'appAssassin',
    team: 'assassin',
    expansion: 'vampire',
    tags: ['individualWin', 'marks'],
    night: [
      // wakes while the Assassin's eyes are still open: they see each other
      step(
        'appAssassin.wake',
        select(0.175, ['identities'], ['marks'], { phantom: { minSec: 3, maxSec: 8 } }),
      ),
    ],
  },
  marksman: {
    id: 'marksman',
    team: 'village',
    expansion: 'vampire',
    tags: ['marks'],
    night: [
      step(
        'marksman.view',
        select(5.25, ['seatCards', 'tokens', 'marks'], ['touched'], { phantom: { minSec: 3, maxSec: 10 } }),
      ),
    ],
  },
  pickpocket: {
    id: 'pickpocket',
    team: 'village',
    expansion: 'vampire',
    tags: ['marks'],
    night: [
      step('pickpocket.steal', select(6.15, ['marks'], ['marks'], { phantom: { minSec: 3, maxSec: 8 } })),
    ],
  },
  villageIdiot: {
    id: 'villageIdiot',
    team: 'village',
    expansion: 'daybreak',
    tags: [],
    night: [
      step(
        'villageIdiot.move',
        select(7.05, ['tokens'], ['seatCards', 'touched'], { phantom: { minSec: 3, maxSec: 10 } }),
      ),
    ],
  },
  // Vampire: the Vampires wake first at dusk. A player with the Mark of the Vampire is a Vampire too; a player who
  // sees a Mark of Fear on their mark does nothing at night (the planner leaves their steps out).
  vampire: {
    id: 'vampire',
    team: 'vampire',
    expansion: 'vampire',
    tags: ['vampire', 'evil', 'marks'],
    night: [VAMPIRES_WAKE()],
  },
  count: {
    id: 'count',
    team: 'vampire',
    expansion: 'vampire',
    tags: ['vampire', 'evil', 'marks'],
    night: [
      VAMPIRES_WAKE(),
      step(
        'count.fear',
        select(0.115, ['identities', 'marks'], ['marks'], { phantom: { minSec: 3, maxSec: 8 } }),
      ),
    ],
  },
  master: {
    id: 'master',
    team: 'vampire',
    expansion: 'vampire',
    tags: ['vampire', 'evil', 'marks'],
    night: [VAMPIRES_WAKE()],
    vote: { savedByVampires: true },
  },
  // Alien: all of these wake with the Aliens and share their action
  syntheticAlien: {
    id: 'syntheticAlien',
    // wakes as an Alien, but wins only by dying (and then the village and the Aliens lose)
    team: 'synthetic',
    expansion: 'alien',
    tags: ['alien', 'individualWin', 'needsNumbers'],
    night: [ALIENS_WAKE()],
  },
  groob: {
    id: 'groob',
    // with Zerb in play too, each of them wins only if the other dies (scoring)
    team: 'alien',
    expansion: 'alien',
    tags: ['alien', 'evil', 'needsNumbers'],
    night: [ALIENS_WAKE(), GROOB_ZERB_WAKE()],
  },
  zerb: {
    id: 'zerb',
    team: 'alien',
    expansion: 'alien',
    tags: ['alien', 'evil', 'needsNumbers'],
    night: [ALIENS_WAKE(), GROOB_ZERB_WAKE()],
  },
  bodySnatcher: {
    id: 'bodySnatcher',
    team: 'alien',
    expansion: 'bonus',
    tags: ['alien', 'evil', 'needsNumbers'],
    randoms: [
      { id: 'anyPlayer', weight: 25 },
      { id: 'neighbor', weight: 15 },
      { id: 'evenPlayer', weight: 10 },
      { id: 'oddPlayer', weight: 10 },
      { id: 'higherPlayer', weight: 10 },
      { id: 'lowerPlayer', weight: 10 },
      { id: 'center', weight: 15 },
      { id: 'pretend', weight: 5 },
    ],
    night: [
      ALIENS_WAKE(),
      step(
        'bodySnatcher.snatch',
        select(
          1.7,
          ['seatCards', 'centerCards', 'tokens'],
          ['seatCards', 'centerCards', 'identities', 'touched'],
          {
            phantom: { minSec: 4, maxSec: 12, announce: true },
          },
        ),
      ),
    ],
  },
  rascal: {
    id: 'rascal',
    team: 'village',
    expansion: 'alien',
    tags: ['needsNumbers'],
    randoms: [
      { id: 'troublemaker', weight: 25 },
      { id: 'troublemakerNeighbors', weight: 10 },
      { id: 'troublemakerEven', weight: 5 },
      { id: 'troublemakerOdd', weight: 5 },
      { id: 'troublemakerHigher', weight: 5 },
      { id: 'troublemakerLower', weight: 5 },
      { id: 'robber', weight: 15 },
      { id: 'witch', weight: 10 },
      { id: 'idiot', weight: 10 },
      { id: 'drunk', weight: 10 },
    ],
    night: [
      step(
        'rascal.act',
        select(7.3, ['seatCards', 'centerCards', 'tokens'], ['seatCards', 'centerCards', 'touched'], {
          phantom: { minSec: 4, maxSec: 12, announce: true },
        }),
      ),
    ],
  },
  empath: {
    id: 'empath',
    team: 'village',
    expansion: 'bonus',
    tags: [],
    randoms: [
      { id: 'empath', weight: 1 },
      { id: 'goodLooking', weight: 1 },
      { id: 'suspicious', weight: 1 },
      { id: 'smellsGood', weight: 1 },
      { id: 'sharpDresser', weight: 1 },
      { id: 'awesome', weight: 1 },
      { id: 'everyone', weight: 1 },
      { id: 'smartest', weight: 1 },
      { id: 'friendliest', weight: 1 },
      { id: 'funniest', weight: 1 },
      { id: 'winner', weight: 1 },
      { id: 'like', weight: 1 },
      { id: 'nobody', weight: 1 },
      { id: 'nicest', weight: 1 },
    ],
    // everyone points first (TABLE_STEPS), then the Empath sees who pointed at whom
    night: [step('empath.watch', info(9.42, ['pointing'], { phantom: { minSec: 3, maxSec: 8 } }))],
  },
}

export const roleDef = (id: RoleId): RoleDef => ROLES[id]
export const hasTag = (id: RoleId, tag: RoleTag): boolean => ROLES[id].tags.includes(tag)
export const isWolf = (id: RoleId) => hasTag(id, 'wolf')
export const isAwakeWolf = (id: RoleId) => hasTag(id, 'awakeWolf')
export const isAlien = (id: RoleId) => hasTag(id, 'alien')
export const isEvilCard = (id: RoleId) => hasTag(id, 'evil')
export const isTanner = (id: RoleId) => hasTag(id, 'tanner')
export const isSeer = (id: RoleId) => hasTag(id, 'seer')
export const isVampire = (id: RoleId) => hasTag(id, 'vampire')
/** The team a card belongs to when the village must kill it (wolves, aliens, vampires), else null. */
export const evilTeamOfRole = (id: RoleId): TeamId | null => (isEvilCard(id) ? ROLES[id].team : null)

/** Default card a team falls back to when a team member exists but no card of that team is in play. */
export const defaultCardForTeam = (team: TeamId): RoleId => {
  switch (team) {
    case 'werewolves':
      return 'werewolf'
    case 'alien':
      return 'alien'
    case 'blob':
      return 'blob'
    case 'mortician':
      return 'mortician'
    case 'tanner':
      return 'tanner'
    default:
      return 'villager'
  }
}

/** All roles' night steps in wake order (a step id shared by several roles appears once). */
export const NIGHT_STEPS: NightStepDef[] = Object.values(ROLES)
  .flatMap((r) => r.night ?? [])
  .filter((s, i, all) => all.findIndex((o) => o.id === s.id) === i)
  .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id))

/**
 * Steps the table takes rather than one role (the engine decides who takes part): when marks are in play,
 * everyone views their own mark at the end of dusk, then the players with a Mark of Love wake to see each other
 * (with Cupid in the deck, a pause stands in for them when nobody is in love); with the Empath in the deck,
 * everyone points for the Empath near the end of the night.
 */
export const TABLE_STEPS: NightStepDef[] = [
  step('marks.view', info(0.18, ['marks'], { barrier: true })),
  step('lovers.wake', info(0.19, ['marks'], { phantom: { minSec: 3, maxSec: 8 } })),
  // with the Empath in the deck, everyone points (eyes closed) at the player the question asks for, once every card
  // has moved (she wakes near the end of the night)
  step('empath.point', select(9.41, [], ['pointing'], { timeoutSec: 25, final: true })),
]

/**
 * The Doppelgänger (the official rules of every expansion). She looks at another player's card and becomes that
 * role; how she then plays each of its night steps:
 *  - 'immediately': she does it inside her own step, right after copying (the Seer's look, the Robber's theft…);
 *  - 'with': she takes the step at its own time, with everyone else who has it (the wolves, the Masons, the
 *    Vampires, the Aliens);
 *  - `{ after }`: she wakes again on her own right after the original, at that order (the Insomniac, the
 *    Revealer, the roles the app gives an instruction to…); such a step is `doppelganger/<step>`, and when she did
 *    not copy that role a pause stands in for it, since the app calls "Doppelgänger, if you viewed…" regardless;
 *  - 'none': nothing (she follows the card: a Copycat's copy, the Oracle's answer, the team Nostradamus announces).
 * With marks in play she wakes before the Vampires (DOPPELGANGER_DUSK_ORDER, the "-7" token), so a copied dusk
 * role's marks go down at dusk; otherwise after the Sentinel.
 */
export type DoppelgangerPlay = 'immediately' | 'with' | 'none' | { after: number }
export const DOPPELGANGER_DUSK_ORDER = 0.105
export const DOPPELGANGER_PLAY: Record<string, DoppelgangerPlay> = {
  'oracle.question': 'none',
  'copycat.copy': 'none',
  'vampires.wake': 'with',
  'count.fear': { after: 0.116 },
  'renfield.wake': { after: 0.121 },
  'diseased.mark': 'immediately',
  'cupid.love': 'immediately',
  'instigator.mark': 'immediately',
  'priest.cleanse': { after: 0.161 },
  'assassin.mark': { after: 0.171 },
  'appAssassin.wake': { after: 0.176 },
  'sentinel.shield': 'immediately',
  'alien.wake': 'with',
  'cow.wake': 'with',
  'groobZerb.wake': 'with',
  'bodySnatcher.snatch': { after: 1.71 },
  'wolves.wake': 'with',
  'alphaWolf.swap': 'immediately',
  'mysticWolf.view': 'immediately',
  'minion.wake': 'immediately',
  'appTanner.wake': { after: 3.11 },
  'leader.wake': { after: 3.21 },
  'mason.wake': 'with',
  'thing.touch': 'immediately',
  'seer.view': 'immediately',
  'appSeer.view': 'immediately',
  'paranormalInvestigator.view': 'immediately',
  'marksman.view': { after: 5.26 },
  'nostradamus.view': 'none',
  'psychic.view': { after: 5.41 },
  'robber.rob': 'immediately',
  'witch.brew': 'immediately',
  'pickpocket.steal': { after: 6.16 },
  'troublemaker.swap': 'immediately',
  'villageIdiot.move': 'immediately',
  'auraSeer.wake': { after: 7.11 },
  'gremlin.swap': { after: 7.21 },
  'rascal.act': { after: 7.31 },
  'drunk.swap': 'immediately',
  'insomniac.wake': { after: 9.05 },
  'squire.wake': { after: 9.11 },
  'beholder.wake': { after: 9.21 },
  'revealer.reveal': { after: 9.35 },
  'exposer.expose': { after: 9.405 },
  // everyone points again, at a second question, for a Doppelgänger-Empath; then she sees who pointed at whom
  'empath.point': { after: 9.425 },
  'empath.watch': { after: 9.427 },
  'curator.artifact': { after: 9.46 },
  'blob.wake': { after: 9.51 },
  'mortician.view': { after: 9.61 },
}
export const doppelgangerPlay = (stepId: string): DoppelgangerPlay => {
  const play = DOPPELGANGER_PLAY[stepId]
  if (!play) throw new Error(`no Doppelgänger play for night step ${stepId}`)
  return play
}

/** A step the Doppelgänger takes on her own after the original role: `doppelganger/<the role's step>`. */
export const DOPPELGANGER_PREFIX = 'doppelganger/'
export const doppelgangerStepId = (stepId: string) => `${DOPPELGANGER_PREFIX}${stepId}`
export const isDoppelgangerStep = (stepId: string) =>
  stepId === 'doppelganger.copy' || stepId.startsWith(DOPPELGANGER_PREFIX)
/** The role's own step behind a Doppelgänger step (the program to run), or the step itself. */
export const baseStepId = (stepId: string) =>
  stepId.startsWith(DOPPELGANGER_PREFIX) ? stepId.slice(DOPPELGANGER_PREFIX.length) : stepId

/**
 * The Doppelgänger's own copy of a step she takes after the original: it also reads what the original writes,
 * so it always lands in a later batch and sees what the original did. Its pause is shorter than a role's: the
 * app's "Doppelgänger, if you viewed…" calls are quick.
 */
const doppelgangerStep = (id: string): NightStepDef => {
  const base = nightStep(baseStepId(id))
  const play = doppelgangerPlay(base.id)
  if (typeof play !== 'object') throw new Error(`${base.id} is not a step the Doppelgänger takes on her own`)
  const reads = [...new Set([...base.reads, ...base.writes.filter((w) => w !== 'touched')])]
  return {
    ...base,
    id,
    order: play.after,
    reads,
    phantom: { minSec: 2, maxSec: 6, ...(base.phantom?.announce ? { announce: true } : {}) },
  }
}

export const nightStep = (id: string): NightStepDef => {
  if (id.startsWith(DOPPELGANGER_PREFIX)) return doppelgangerStep(id)
  const s = NIGHT_STEPS.find((x) => x.id === id) ?? TABLE_STEPS.find((x) => x.id === id)
  if (!s) throw new Error(`unknown night step ${id}`)
  return s
}
